import React, { useState, useRef } from 'react';
import { X, Image as ImageIcon, Send } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { uploadMedia } from '../../lib/storage';
import { UserMentionInput } from '../ui/UserMentionInput';
import { Avatar } from '../Profile/Avatar';
import type { ConversationNiche } from '../../types/conversations';
import { toast } from 'sonner';
import { notifyFollowers } from '../../lib/follows';
import { GifPicker } from '../ui/GifPicker';
import { GifIcon } from '../ui/GifIcon';

interface CreateConversationPostFormProps {
  niche: ConversationNiche;
  nicheLabel: string;
  onSuccess: () => void;
  userProfile?: {
    username?: string;
    avatar_url?: string;
  };
}

export function CreateConversationPostForm({ 
  niche, 
  nicheLabel, 
  onSuccess,
  userProfile 
}: CreateConversationPostFormProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [gifUrls, setGifUrls] = useState<string[]>([]);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mentionedUsers, setMentionedUsers] = useState<string[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      toast.error('Please fill in title and content');
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please log in to create a post');
        return;
      }

      // Upload images/videos and include GIF URLs (no upload needed)
      let mediaUrls: string[] = [...gifUrls];
      if (mediaFiles.length > 0) {
        const uploaded = await Promise.all(
          mediaFiles.map(async (file) => {
            try {
              const bucket = file.type.startsWith('video/') ? 'post-videos' : 'post-images';
              return await uploadMedia(file, bucket);
            } catch (error) {
              console.error('Error uploading:', error);
              return null;
            }
          })
        );
        mediaUrls = [...mediaUrls, ...uploaded.filter(Boolean) as string[]];
      }

      // Generate slug from title
      const slug = title.trim()
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 80)
        + '-' + Date.now().toString(36);

      const { error } = await supabase
        .from('conversation_posts')
        .insert([{
          user_id: user.id,
          niche,
          title: title.trim(),
          content: content.trim(),
          images: mediaUrls,
          slug
        }]);

      if (error) throw error;

      // Notify followers about the new conversation post
      notifyFollowers({
        userId: user.id,
        username: userProfile?.username || 'Someone',
        avatarUrl: userProfile?.avatar_url,
        type: 'follower_new_post',
        title: 'New Conversation Post',
        message: `${userProfile?.username || 'Someone'} posted "${title.trim()}"`,
        data: {
          post_type: 'conversation',
          niche,
          slug,
          content_preview: title.trim()
        }
      });

      setTitle('');
      setContent('');
      setMediaFiles([]);
      setGifUrls([]);
      setMentionedUsers([]);
      setIsExpanded(false);
      toast.success('Post created successfully!');
      onSuccess();
    } catch (error: any) {
      console.error('Error creating post:', error);
      toast.error(error.message || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  const handleMediaUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(
      (f) => f.type.startsWith('image/') || f.type.startsWith('video/')
    );
    if (files.length + totalMediaCount > 4) {
      toast.error('Maximum 4 images/videos allowed');
      return;
    }
    setMediaFiles((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const removeMedia = (index: number) => {
    setMediaFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const addGif = (url: string) => {
    if (mediaFiles.length + gifUrls.length >= 4) {
      toast.error('Maximum 4 images/videos allowed');
      return;
    }
    setGifUrls((prev) => [...prev, url]);
  };

  const removeGif = (index: number) => {
    setGifUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const totalMediaCount = mediaFiles.length + gifUrls.length;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
      <form onSubmit={handleSubmit}>
        <div className="flex space-x-3">
          <Avatar
            url={userProfile?.avatar_url}
            size="sm"
            username={userProfile?.username}
            editable={false}
          />
          <div className="flex-1">
            {!isExpanded ? (
              <button
                type="button"
                onClick={() => setIsExpanded(true)}
                className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-gray-500 transition-colors"
              >
                Start a conversation in {nicheLabel}...
              </button>
            ) : (
              <div className="space-y-3">
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Give your post a title..."
                  className="w-full px-4 py-3 bg-gray-50 rounded-xl border-0 focus:ring-2 focus:ring-accent-text/20 font-medium"
                  autoFocus
                />
                <UserMentionInput
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  onMentionedUsersChange={setMentionedUsers}
                  placeholder="Share your thoughts, questions, or experiences..."
                  className="w-full px-4 py-3 bg-gray-50 rounded-xl border-0 focus:ring-2 focus:ring-accent-text/20 resize-none min-h-[100px]"
                  rows={4}
                />

                {(mediaFiles.length > 0 || gifUrls.length > 0) && (
                  <div className="flex flex-wrap gap-2">
                    {gifUrls.map((url, index) => (
                      <div key={`gif-${index}`} className="relative">
                        <img src={url} alt="GIF" className="h-20 w-20 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => removeGif(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    {mediaFiles.map((file, index) => (
                      <div key={index} className="relative">
                        {file.type.startsWith('video/') ? (
                          <video src={URL.createObjectURL(file)} className="h-20 w-20 object-cover rounded-lg" muted loop />
                        ) : (
                          <img src={URL.createObjectURL(file)} alt="" className="h-20 w-20 object-cover rounded-lg" />
                        )}
                        <button
                          type="button"
                          onClick={() => removeMedia(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowGifPicker(true)}
                      disabled={totalMediaCount >= 4}
                      className="p-2 text-gray-500 hover:text-accent-text hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                      title="Add GIF"
                    >
                      <GifIcon className="h-5 w-5 text-current" />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,video/*"
                      onChange={handleMediaUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={totalMediaCount >= 4}
                      className="p-2 text-gray-500 hover:text-accent-text hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                      title="Add photo or video"
                    >
                      <ImageIcon className="h-5 w-5" />
                    </button>
                    <span className="text-xs text-gray-400">{totalMediaCount}/4</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsExpanded(false);
                        setTitle('');
                        setContent('');
                        setMediaFiles([]);
                        setGifUrls([]);
                      }}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading || !title.trim() || !content.trim()}
                      className="px-5 py-2 bg-accent-text text-white rounded-lg hover:bg-accent-text/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm font-medium"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Posting...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          Post
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </form>
      <GifPicker
        isOpen={showGifPicker}
        onClose={() => setShowGifPicker(false)}
        onSelect={addGif}
      />
    </div>
  );
}
